import { createNewLayerState, LayerState, normalizeLayerState } from '../../layerState';
import { type LineColor } from '@visualizer/types';
import { lineFixedColors, lineSingleGradient, LinesColorModeOptions } from '../../ColorScheme';
import { BucketBase, type BucketMigration } from './BucketBase';

/* VERSIONING GUIDE:
 1) Add update in the specific state (globalState, layerState, DesignSchemeState)
 2) Bump latestVersion when the persisted schema changes.
 3) Add migrateLayersBucketV# function where the change is made
 4) Register it in LAYERS_BUCKET_MIGRATIONS with key N.
EXAMPLE:
 - Add createGlobalState function (gobalState file) enableAutoSave : boolean in  globalState.settings
 - Bump latestVersion in GlobalBucket to 2
 - Add migrateLayersBucketV2 function like this:
    const migrateGlobalBucketV2 = (state: GlobalBucketState): GlobalBucketState => ({
        ...state,
        settings: {
            ...createGlobalState().settings,
            ...state.settings,
            enableAutoSave: state.settings?.enableAutoSave ?? createGlobalState().settings.enableAutoSave,
        },
    }); 
 - Add in LAYERS_BUCKET_MIGRATIONS: 2: migrateGlobalBucketV2
 */

export type LayersBucketState = {
    activeLayerID: string;
    layerStates: Record<string, LayerState>;
    failedLayers: Record<string, string>;
};

export class LayersBucket extends BucketBase<LayersBucketState> {
    readonly id = 'layers';

    readonly latestVersion = 4;

    protected getMigrations(): Record<number, BucketMigration<LayersBucketState>> {
        return LAYERS_BUCKET_MIGRATIONS;
    }

    getDefault(): LayersBucketState {
        return {
            activeLayerID: '',
            layerStates: {},
            failedLayers: {},
        };
    }

    migrate(persisted: unknown, version = 0): LayersBucketState {
        const defaults = this.getDefault();
        const persistedState = (persisted as Partial<LayersBucketState> | undefined) ?? undefined;
        const baseState: LayersBucketState = persistedState
            ? {
                  activeLayerID: persistedState.activeLayerID ?? defaults.activeLayerID,
                  layerStates: persistedState.layerStates ?? defaults.layerStates,
                  failedLayers: persistedState.failedLayers ?? defaults.failedLayers,
              }
            : defaults;

        const migratedState = version >= this.latestVersion ? baseState : this.applyMigrations(baseState, version);
        return normalizeLayersBucketState(migratedState);
    }
}

const normalizeLayersBucketState = (state: LayersBucketState): LayersBucketState => ({
    ...state,
    layerStates: Object.fromEntries(
        Object.entries(state.layerStates ?? {}).map(([id, layer]) => [id, normalizeLayerState(layer, layer?.geodataId ?? '')])
    ) as Record<string, LayerState>,
    failedLayers: state.failedLayers ?? {},
});

const migrateLayersBucketV1 = (state: LayersBucketState): LayersBucketState => state;

const migrateLayersBucketV2 = (state: LayersBucketState): LayersBucketState => ({
    ...state,
    layerStates: Object.fromEntries(
        Object.entries(state.layerStates ?? {}).map(([id, layer]) => [
            id,
            {
                ...layer,
                performanceMetrics: {
                    thresholdLevel: null,
                },
            },
        ])
    ) as Record<string, LayerState>,
});

const migrateLayersBucketV3 = (state: LayersBucketState): LayersBucketState => ({
    ...state,
    layerStates: Object.fromEntries(
        Object.entries(state.layerStates ?? {}).map(([id, layer]) => [
            id,
            {
                ...createNewLayerState(),
                ...layer,
                geodataId: layer?.geodataId ?? '',
            },
        ])
    ) as Record<string, LayerState>,
});

const migrateLayersBucketV4 = (state: LayersBucketState): LayersBucketState => ({
    ...state,
    layerStates: Object.fromEntries(
        Object.entries(state.layerStates ?? {}).map(([id, layer]) => [
            id,
            {
                ...layer,
                simulationItems: {
                    ...layer.simulationItems,
                    linesColorModeOption: resolveLinesColorModeOptionFromLineColor(layer.simulationItems?.linesColorMode),
                },
            },
        ])
    ) as Record<string, LayerState>,
});

const isSameRange = (a: number[], b: number[]): boolean => a[0] === b[0] && a[1] === b[1];

const resolveLinesColorModeOptionFromLineColor = (lineColor: LineColor | undefined): LinesColorModeOptions => {
    if (!lineColor) return LinesColorModeOptions.MultiGradient;
    if (lineColor.isBicolor) return LinesColorModeOptions.BiColor;

    if (
        isSameRange(lineColor.Lines_SUN_H, lineSingleGradient.Lines_SUN_H) &&
        isSameRange(lineColor.Lines_VIEW_H, lineSingleGradient.Lines_VIEW_H) &&
        isSameRange(lineColor.Lines_NOISE_H, lineSingleGradient.Lines_NOISE_H) &&
        isSameRange(lineColor.Lines_WIND_H, lineSingleGradient.Lines_WIND_H)
    ) {
        return LinesColorModeOptions.SingleGradient;
    }

    if (
        isSameRange(lineColor.Lines_SUN_H, lineFixedColors.Lines_SUN_H) &&
        isSameRange(lineColor.Lines_VIEW_H, lineFixedColors.Lines_VIEW_H) &&
        isSameRange(lineColor.Lines_NOISE_H, lineFixedColors.Lines_NOISE_H) &&
        isSameRange(lineColor.Lines_WIND_H, lineFixedColors.Lines_WIND_H)
    ) {
        return LinesColorModeOptions.FixedColors;
    }

    return LinesColorModeOptions.MultiGradient;
};

const LAYERS_BUCKET_MIGRATIONS: Record<number, BucketMigration<LayersBucketState>> = {
    1: migrateLayersBucketV1,
    2: migrateLayersBucketV2,
    3: migrateLayersBucketV3,
    4: migrateLayersBucketV4,
};
